import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import StyledRadio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { getIn } from 'formik';
import noop from 'lodash/noop';

import Box from '@mui/material/Box';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import CircleCheckedFilled from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import { DatePicker, TimePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Grid from '@mui/material/Grid';

import RTAutocomplete from '../RTAutocomplete';
import { isDateValid } from '../../utils/dateUtils';

import renderInputStyles from './renderInputStyles';

function RenderInput({
    icon = <CheckBoxOutlineBlankIcon />,
    checkedIcon = <CheckBoxIcon />,
    roundCheckbox = false,
    disabled = false,
    field = { onBlur: noop, onChange: noop, onFocus: noop, name: '' },
    size = 'medium',
    form = { setFieldValue: noop, setTouched: noop, setFieldTouched: noop, values: {} },
    fullWidth = false,
    label = '',
    multiline = false,
    rows = 0,
    type = 'text',
    variant = 'outlined',
    dateVariant = 'inline',
    inputVariant = 'outlined',
    options = [],
    className = '',
    inputProps = {},
    handleChangeCallback = null,
    endAdornment = <span />,
    startAdornment = <span />,
    SelectProps = {
        MenuProps: {
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
        }
    },
    color = 'secondary',
    placeholder = '',
    radioMultiRows = false,
    radioButtonClasses = {},
    InputProps = {},
    InputAdornmentProps = {},
    radioDivider = false,
    customLabelElement = null,
    isTemplate = false,
    customLabel = 'custom',
    styleFormControlLabel = '',
    radioGroupStyle = '',
    getOptionSelected = (option) => option._id,
    getOptionLabel = (option) => option.name,
    renderOption = (option) => <>{option.name}</>,
    filter = (option) => option.name,
    required = false,
    helperText = '',
    keyboardIcon = <EventIcon />,
    ampm = true,
    autoOk = false,
    mask = '',
    format = '',
    disableHelperText = false,
    maxDate,
    maxDateMessage = '',
    minDate,
    emptyLabel = '',
    minDateMessage = '',
    minutesStep = 1,
    disableExtraSpacing = false,
    formControlClassName = '',
    defaultValue,
    shrink = true,
    indeterminate = false,
    disableFuture = false,
    height = 'auto',
    showDirections = false
}) {
    const classes = renderInputStyles({ isTemplate });
    const { errors, touched, setFieldTouched, status, setFieldValue, values } = form;
    const { name, value, onChange, onBlur, onFocus } = field;

    const errorText = useMemo(() => (name ? getIn(errors, name) : ''), [errors, name]);
    const touchedVal = useMemo(() => (name ? getIn(touched, name) : null), [name, touched]);
    const hasError = useMemo(
        () => (!!touchedVal && !!errorText) || (!!status && !!status[name]),
        [errorText, name, status, touchedVal]
    );

    const handleOnChange = useCallback(
        (e, v) => {
            if (onChange) {
                onChange(e, v);
            }
            if (handleChangeCallback) {
                handleChangeCallback(e, v);
            }
        },
        [handleChangeCallback, onChange]
    );

    const handleOnFocus = useCallback(
        (e, v) => {
            if (onFocus) {
                onFocus(e, v);
            }
            if (!onChange && setFieldTouched) {
                // For Uncontrolled Fields. Use setFieldTouched so nested/dotted
                // field names write the correct nested touched path. Guard for
                // callers that pass a partial `form` prop without setFieldTouched.
                setFieldTouched(name, false, false);
            }
        },
        [name, onChange, onFocus, setFieldTouched]
    );

    const handleOnBlur = useCallback(
        (e) => {
            // onBlur is Formik handleBlur
            if (onBlur) {
                if (type === 'text') {
                    setFieldValue(name, e.target.value.trim());
                } else {
                    onBlur(e);
                }
            }
            if (!touchedVal && setFieldTouched) {
                setTimeout(() => {
                    // setFieldTouched writes the correct nested path (getIn reads
                    // nested), so validation errors surface for dotted names too.
                    // Guarded for callers passing a partial `form` prop.
                    setFieldTouched(name, true);
                }, 0);
            }
        },
        [name, onBlur, setFieldValue, setFieldTouched, touchedVal, type]
    );

    const getMessageError = useMemo(() => {
        if (disableHelperText) {
            return '';
        }
        if (showDirections && errorText) {
            return <div style={{ color: 'red', fontSize: '11px', paddingTop: '4px' }}>{errorText}</div>;
        }
        if (touchedVal && errorText) {
            return errorText;
        }
        if (status) {
            return getIn(status, name);
        }
        return typeof helperText === 'string' ? helperText : ' ';
    }, [disableHelperText, errorText, helperText, name, status, touchedVal]);

    const handleChangeDateOrTimePicker = useCallback(
        (newDate) => {
            const isNewDateValid = newDate && isDateValid(newDate);
            const currFieldDate = value ? `${value}` : name ? getIn(values, name) : new Date();
            if (isNewDateValid || !currFieldDate || (!isNewDateValid && isDateValid(currFieldDate))) {
                if (handleChangeCallback) {
                    handleChangeCallback(newDate || '');
                } else {
                    setFieldValue(name, newDate || '');
                }
            }
            if (setFieldTouched && (isNewDateValid || !touchedVal)) {
                setTimeout(() => {
                    // setFieldTouched writes the correct nested path so date
                    // validation errors (e.g. end-before-start) surface for
                    // dotted field names like duplicatedDataByInputs.duplicateEndDate.
                    setFieldTouched(name, true);
                }, 0);
            }
        },
        [handleChangeCallback, name, setFieldValue, setFieldTouched, touchedVal, value, values]
    );

    // const handleDateTimeError = useCallback(
    //     (err) => {
    //         setDateTimeHelperText(() => {
    //             if (err) {
    //                 return err;
    //             } else {
    //                 return (touchedVal && errorText) || helperText;
    //             }
    //         });
    //     },
    //     [errorText, helperText, touchedVal]
    // );

    if (type === 'select') {
        return (
            <FormControl variant="standard" fullWidth={fullWidth} component="div">
                <TextField
                    select
                    size={size}
                    disabled={disabled}
                    variant={variant}
                    multiline={multiline}
                    rows={rows}
                    label={label}
                    value={value ?? ''}
                    name={name}
                    onChange={handleOnChange}
                    error={hasError}
                    required={required}
                    helperText={getMessageError}
                    className={className}
                    InputProps={InputProps}
                    InputLabelProps={{ shrink }}
                    SelectProps={{
                        ...SelectProps,
                        MenuProps: {
                            ...SelectProps.MenuProps,
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'center'
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'center'
                            },
                            MenuListProps: {
                                disablePadding: true
                            }
                        }
                    }}
                >
                    {options.map((v) => (
                        <MenuItem key={v._id} value={v._id}>
                            {v.name}
                        </MenuItem>
                    ))}
                </TextField>
            </FormControl>
        );
    }
    if (type === 'autocomplete') {
        return (
            <RTAutocomplete
                type="select"
                options={options}
                getOptionSelected={getOptionSelected}
                getOptionLabel={getOptionLabel}
                label={label}
                renderOption={renderOption}
                textFieldProps={{ required, fullWidth }}
                fullWidth={fullWidth}
                name={name}
                spacing={2}
                className={className}
                classes={{
                    listbox: classes.listbox,
                    option: classes.option
                }}
                placeholder={placeholder}
                filter={filter}
                field={field}
                onChangeText={handleOnChange}
                form={form}
                isDisplayTextError={false}
                helperText={getMessageError}
                required={required}
                disabled={disabled}
            />
        );
    }
    if (type === 'checkbox') {
        return (
            <FormControlLabel
                className={styleFormControlLabel}
                control={
                    <Checkbox
                        onChange={handleOnChange}
                        color={color}
                        name={name}
                        required={required}
                        label={label}
                        checked={value}
                        className={`${className} ${roundCheckbox ? classes.roundCheckbox : ''}`}
                        icon={roundCheckbox ? <CircleUnchecked /> : icon}
                        checkedIcon={roundCheckbox ? <CircleCheckedFilled /> : checkedIcon}
                        disabled={disabled}
                        indeterminate={indeterminate}
                    />
                }
                label={label}
            />
        );
    }
    if (type === 'radio') {
        // must get:
        // value={values.__nameOfInput__}
        return (
            <FormControl variant="standard" component="fieldset">
                <RadioGroup
                    disabled={disabled}
                    value={value}
                    name={name}
                    onChange={handleOnChange}
                    row={!radioMultiRows}
                    className={radioGroupStyle}
                >
                    {label && (
                        <FormLabel component="legend" required={required}>
                            <Grid container alignItems="center" className={classes.height100}>
                                {label}
                            </Grid>
                        </FormLabel>
                    )}
                    {!radioMultiRows && !disableExtraSpacing && <Grid className={classes.width1Spacing} />}
                    {options.map((v, index) => (
                        <Box key={v._id} className={radioMultiRows ? classes.width100 : ''}>
                            <FormControlLabel
                                value={v._id}
                                label={
                                    customLabel && customLabelElement && v._id === customLabel
                                        ? customLabelElement
                                        : v.name
                                }
                                className={className}
                                control={
                                    <StyledRadio
                                        color={color}
                                        name={name}
                                        classes={radioButtonClasses}
                                        disabled={disabled}
                                    />
                                }
                            >
                                {v.name}
                            </FormControlLabel>
                            {radioDivider && index !== options.length - 1 && <Divider />}
                        </Box>
                    ))}
                </RadioGroup>
            </FormControl>
        );
    }

    if (type === 'date') {
        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label={label}
                    value={value ? new Date(value) : null}
                    onChange={handleChangeDateOrTimePicker}
                    format={format}
                    maxDate={maxDate}
                    minDate={minDate}
                    disabled={disabled}
                    disableFuture={disableFuture}
                    className={`${classes.adornedStyle} ${classes.minWidth120px} ${className}`}
                    slotProps={{
                        textField: {
                            fullWidth,
                            variant: inputVariant || 'standard',
                            placeholder,
                            // Surface the Formik/yup validation message (e.g. end-before-start)
                            // like the text/number inputs do — falls back to the static
                            // helperText when there's no error.
                            helperText: getMessageError,
                            error: hasError,
                            required,
                            InputLabelProps: { shrink },
                            InputProps: {
                                ...InputProps,
                                style: { height: height || 'auto' }
                            }
                        },
                        inputAdornment: InputAdornmentProps,
                        openPickerButton: keyboardIcon ? { children: keyboardIcon } : undefined
                    }}
                />
            </LocalizationProvider>
        );
    }

    if (type === 'time') {
        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                    label={label}
                    value={value ? new Date(value) : null}
                    onChange={handleChangeDateOrTimePicker}
                    minutesStep={minutesStep}
                    ampm={ampm}
                    disabled={disabled}
                    disableFuture={disableFuture}
                    className={`${classes.adornedStyle} ${classes.minWidth120px} ${className}`}
                    slotProps={{
                        textField: {
                            fullWidth,
                            variant: inputVariant || 'standard',
                            placeholder,
                            // Surface the Formik/yup validation message (e.g. end-before-start)
                            // like the text/number inputs do — falls back to the static
                            // helperText when there's no error.
                            helperText: getMessageError,
                            error: hasError,
                            required,
                            InputLabelProps: { shrink },
                            InputProps: {
                                ...InputProps,
                                style: { height: height || 'auto' }
                            }
                        },
                        inputAdornment: InputAdornmentProps,
                        openPickerButton: keyboardIcon ? { children: keyboardIcon } : undefined
                    }}
                />
            </LocalizationProvider>
        );
    }

    if (type === 'dateAndTime') {
        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                    label={label}
                    value={value ? new Date(value) : null}
                    onChange={handleChangeDateOrTimePicker}
                    format={format}
                    minutesStep={minutesStep}
                    ampm={ampm}
                    maxDate={maxDate}
                    minDate={minDate}
                    disabled={disabled}
                    disableFuture={disableFuture}
                    className={`${classes.adornedStyle} ${classes.minWidth120px} ${className}`}
                    slotProps={{
                        textField: {
                            fullWidth,
                            variant: inputVariant || 'standard',
                            placeholder,
                            // Surface the Formik/yup validation message (e.g. end-before-start)
                            // like the text/number inputs do — falls back to the static
                            // helperText when there's no error.
                            helperText: getMessageError,
                            error: hasError,
                            required,
                            InputLabelProps: { shrink },
                            InputProps: {
                                ...InputProps,
                                style: { height: height || 'auto' }
                            }
                        },
                        inputAdornment: InputAdornmentProps,
                        openPickerButton: keyboardIcon ? { children: keyboardIcon } : undefined
                    }}
                />
            </LocalizationProvider>
        );
    }

    return (
        <FormControl
            variant="standard"
            className={formControlClassName}
            fullWidth={fullWidth}
            component="div"
        >
            <TextField
                className={className}
                disabled={disabled}
                type={type}
                variant={variant}
                multiline={multiline}
                required={required}
                rows={rows}
                label={label}
                {...(defaultValue !== undefined ? { defaultValue } : { value: value ?? '' })}
                name={name}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                onFocus={handleOnFocus}
                placeholder={placeholder}
                error={hasError}
                helperText={getMessageError} // added space to avoid height change on error
                InputProps={{
                    startAdornment,
                    endAdornment,
                    style: {
                        height: height || 'auto'
                    }
                }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                inputProps={inputProps}
            />
        </FormControl>
    );
}

RenderInput.propTypes = {
    roundCheckbox: PropTypes.bool,
    icon: PropTypes.element,
    checkedIcon: PropTypes.element,
    className: PropTypes.string,
    formControlClassName: PropTypes.string,
    customLabelElement: PropTypes.element,
    customLabel: PropTypes.string,
    disabled: PropTypes.bool,
    radioMultiRows: PropTypes.bool,
    type: PropTypes.string,
    size: PropTypes.string,
    color: PropTypes.string,
    placeholder: PropTypes.string,
    field: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.shape({})]),
        onChange: PropTypes.func,
        onBlur: PropTypes.func,
        onFocus: PropTypes.func
    }),
    form: PropTypes.shape({
        touched: PropTypes.shape({}),
        errors: PropTypes.shape({}),
        setFieldValue: PropTypes.func,
        setTouched: PropTypes.func,
        status: PropTypes.shape({}),
        values: PropTypes.shape({})
    }),
    SelectProps: PropTypes.shape({
        MenuProps: PropTypes.shape({
            anchorOrigin: PropTypes.shape({
                vertical: PropTypes.oneOf(['bottom', 'center', 'top']),
                horizontal: PropTypes.oneOf(['center', 'left', 'right'])
            })
        })
    }),
    fullWidth: PropTypes.bool,
    multiline: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    options: PropTypes.arrayOf(PropTypes.shape({})),
    variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
    dateVariant: PropTypes.oneOf(['dialog', 'inline', 'static']),
    inputVariant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
    handleChangeCallback: PropTypes.func,
    endAdornment: PropTypes.element,
    startAdornment: PropTypes.element,
    InputProps: PropTypes.shape({}), // ONLY FOR TEXT TYPE CURRENTLY,
    inputProps: PropTypes.shape({}),
    InputAdornmentProps: PropTypes.shape({}),
    radioButtonClasses: PropTypes.shape({}),
    isTemplate: PropTypes.bool,
    styleFormControlLabel: PropTypes.string,
    radioGroupStyle: PropTypes.string,
    getOptionSelected: PropTypes.func,
    getOptionLabel: PropTypes.func,
    renderOption: PropTypes.func,
    filter: PropTypes.func,
    // isUseLocals: PropTypes.bool,
    radioDivider: PropTypes.bool,
    required: PropTypes.bool,
    indeterminate: PropTypes.bool,
    keyboardIcon: PropTypes.element,
    ampm: PropTypes.bool,
    autoOk: PropTypes.bool,
    disableExtraSpacing: PropTypes.bool,
    disableHelperText: PropTypes.bool,
    mask: PropTypes.string,
    helperText: PropTypes.string,
    format: PropTypes.string,
    maxDate: PropTypes.string,
    maxDateMessage: PropTypes.string,
    minDate: PropTypes.string,
    emptyLabel: PropTypes.string,
    minDateMessage: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    minutesStep: PropTypes.number,
    disableFuture: PropTypes.bool,
    shrink: PropTypes.bool,
    style: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    showDirections: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default React.memo(RenderInput);
